var _a;
/**
 * Route wrapper for editing an existing proposal.
 * Loads the proposal data and renders ProposalForm in edit mode.
 *
 * CZNA-602 — the route is also the permission gate, and it is the ONLY gate that
 * counts. `ProposalDetail` and `useProposalReviewActions` hide their "Upravit" /
 * "Upravit v revizi" buttons behind the matching predicate, but hiding a button is
 * not a gate: `/proposals/:id/edit` is reachable by URL, from browser history, and
 * from the picker's propose-GA flow (which returns here with `?restore=`). A user
 * without the role used to get the whole form and learn the truth only on save.
 * Exactly the CZNA-591 defect, which was fixed for CREATING a proposal and left
 * standing here.
 *
 * CZNA-598 — WHETHER the caller may edit is no longer worked out here. It used to be: a
 * three-branch mirror of `ProposalServiceImpl#updateProposal` (PROPOSAL_EDIT org-scoped
 * for DRAFT/CHANGES_REQUESTED plus the CZNA-556 institution rule; PROPOSAL_APPROVE
 * org-scoped for PENDING_REVIEW; PROPOSAL_APPROVE national-only for
 * PENDING_NATIONAL_REVIEW), assembled from three different frontend predicates, each of
 * which had been wrong at least once. The answer now comes from the backend as
 * `proposal.actions` — one evaluation, the same one the PUT enforces.
 *
 * What stays here is WHICH SENTENCE a refusal gets, and that is not the same question. It
 * is derived from the proposal's own status, which the frontend legitimately has:
 *
 *   DRAFT, CHANGES_REQUESTED     → "draft", or "institution" when the caller is not from
 *                                  the originating library (CZNA-556 — a role cannot fix
 *                                  that, so "ask your administrator" would be a lie)
 *   PENDING_REVIEW               → "review"
 *   PENDING_NATIONAL_REVIEW      → "national"
 *   anything else                → not editable by anyone, admin included: "closed",
 *                                  asked BEFORE any permission question
 *
 * The gate is still the only one that counts, for the CZNA-602 reason: hiding a button is
 * not a gate, and `/proposals/:id/edit` is reachable by URL, from browser history, and
 * from the picker's propose-GA flow (which returns here with `?restore=`).
 *
 * KNOWN and accepted: a CZNA-556 refusal raised at SAVE time, rather than here, still
 * collapses to the generic `error.forbidden` ("požádejte správce") via
 * `messageIdForKind` — the advice this screen exists to avoid. Reaching it needs the
 * gate's answer to go stale between mount and save: the proposal moves from a pending
 * state back to CHANGES_REQUESTED while the form is open, or the caller's organization
 * changes inside the authorization context's 5-minute `staleTime`. Saying it honestly
 * there would mean giving the backend refusal its own error code and mapping it, which is
 * a change to the shared error contract rather than to this route. Left as prose on
 * purpose: the case is a race, and the wrong sentence in it is worse than no note about
 * why it is wrong.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Layout, MessageBanner } from '@folio/stripes/components';
import useProposalDetail from '../hooks/useProposalDetail';
import useAuthorizationContext from '../hooks/useAuthorizationContext';
import useOrganizationLabel from '../hooks/useOrganizationLabel';
import useFormConfig from '../hooks/useFormConfig';
import ProposalForm from '../components/ProposalForm/ProposalForm';
import { matchTemplateShape, proposalDataToShapeFields, } from '../components/ProposalForm/utils/templateShapeRouting';
import { ProposalGateLoading, ProposalGateMessage } from '../components/ProposalGate';
import TargetRecordStateBanner from '../components/TargetRecordState';
import { PENDING_PROPOSAL_STATUSES, ProposalStatus, proposalAllows } from '../types/api';
var BACK_LABEL = 'ui-czna-authorities.proposals.edit.denied.back';
/**
 * The reasons a refusal can have — one per permission branch of
 * `ProposalServiceImpl#updateProposal`, and the suffix of the sentence that explains it
 * (`proposals.edit.denied.<reason>.message`).
 *
 * A runtime array, not only a type union, so `proposalEditMessages.test.ts` can
 * enumerate the copy every branch needs. A literal list over there would silently walk
 * past a fourth branch and ship it an untranslated message id.
 */
export var GATE_REASONS = ['draft', 'institution', 'review', 'national'];
/** The states that are editable by nobody. Message per state — "closed" is not a reason. */
var CLOSED_MESSAGES = (_a = {},
    _a[ProposalStatus.APPROVED] = 'ui-czna-authorities.proposals.edit.closed.approved.message',
    _a[ProposalStatus.REJECTED] = 'ui-czna-authorities.proposals.edit.closed.rejected.message',
    _a);
/**
 * The form itself, mounted only once the proposal is loaded and the gates have passed.
 *
 * CZNA-1087 — a separate component for the same reason `RecordEdit` has one: the shape
 * lookup is a hook, and the gates above return early. A proposal does not persist which
 * template it was written on (`templateId` is derived server-side from the setting key,
 * not stored on the proposal), so reopening a draft has to ask the data again — which is
 * the right source anyway: the draft may have been amended into the other shape since.
 */
var ProposalEditForm = function (_a) {
    var id = _a.id, proposal = _a.proposal, isReviewMode = _a.isReviewMode, restoreToken = _a.restoreToken, onCancel = _a.onCancel;
    var defaultShapeConfig = useFormConfig(proposal.recordType).config;
    var shapeTemplateId = matchTemplateShape(defaultShapeConfig === null || defaultShapeConfig === void 0 ? void 0 : defaultShapeConfig.templateRouting, proposalDataToShapeFields(proposal.proposal_data), defaultShapeConfig === null || defaultShapeConfig === void 0 ? void 0 : defaultShapeConfig.templateId);
    return (React.createElement(ProposalForm, { key: "".concat(id, "-").concat(proposal.optimisticLock), recordType: proposal.recordType, templateId: shapeTemplateId, onCancel: onCancel, mode: "edit", proposalId: id, initialData: proposal.proposal_data, proposalStatus: proposal.status, proposalType: proposal.proposalType, targetRecordAuthorityId: proposal.targetRecordAuthorityId, targetRecordExportId: proposal.targetRecordExportId, optimisticLock: proposal.optimisticLock, reviewMode: isReviewMode, canApprove: proposalAllows(proposal, 'approve'), restoreKey: restoreToken, 
        // CZNA-1525 — the same warning as at create: the record's state, and that the change
        // waits for its approval. Whether it may be APPROVED is the detail's approvalBlock.
        notice: proposal.targetRecord
            ? React.createElement(TargetRecordStateBanner, { record: proposal.targetRecord })
            : undefined, metadata: {
            createdBy: proposal.createdBy,
            createdAt: proposal.createdAt,
            updatedBy: proposal.updatedBy,
            updatedAt: proposal.updatedAt,
        } }));
};
var ProposalEdit = function () {
    var _a, _b;
    var intl = useIntl();
    var history = useHistory();
    var id = useParams().id;
    var location = useLocation();
    // CZNA-241: one-shot restore token when this EDIT-mode parent is the return
    // target of the propose-GA-from-picker flow. Threaded down to RecordForm to
    // rehydrate the in-progress edits and write back the new GA's FK + heading.
    var restoreToken = (_a = new URLSearchParams(location.search).get('restore')) !== null && _a !== void 0 ? _a : undefined;
    var _c = useProposalDetail(id), proposal = _c.proposal, isLoading = _c.isLoading, isError = _c.isError;
    var _d = useAuthorizationContext(), isAuthorizationLoading = _d.isLoading, isAuthorizationError = _d.isError, isFromOriginatingInstitution = _d.isFromOriginatingInstitution;
    var _e = useOrganizationLabel(), getOrganizationLabel = _e.getOrganizationLabel, isOrganizationLoading = _e.isLoading;
    var handleCancel = function () {
        history.push("/czna-authorities/proposals/".concat(id));
    };
    /**
     * May the caller open THIS proposal, and which sentence a refusal gets. `null` means no
     * role opens it — the state itself is closed.
     *
     * <p>CZNA-598 — `allowed` is the backend's answer, read from `actions`, for every state.
     * Only the SENTENCE is decided here, and only from the status, which is a fact the
     * frontend already has and does not have to re-derive from permission rows.
     *
     * <p>Deliberately one function rather than a predicate plus a separate list of editable
     * states: the pair would be two places to remember a new ProposalStatus in, and the
     * list is the half nobody would think to update. Here a status that is not named
     * falls through to `null` and gets the "cannot be edited" screen, which fails closed.
     */
    var gateFor = function (target) {
        var allowed = proposalAllows(target, 'edit');
        switch (target.status) {
            case ProposalStatus.DRAFT:
            case ProposalStatus.CHANGES_REQUESTED: {
                // CZNA-556 — two rules can refuse here and they need different sentences. Missing
                // the role ends in "ask your administrator"; being from another library does not,
                // because no role an administrator can grant would help — the answer is to write
                // to the originator. Only asked once `allowed` has said no, so an admin (who
                // passes above and is often from a different library) never sees the second one.
                // This is wording, not a second gate: `isFromOriginatingInstitution` decides
                // nothing about access, and must not be ANDed into `allowed`.
                var reason = allowed || isFromOriginatingInstitution(target.organizationId)
                    ? 'draft'
                    : 'institution';
                return { allowed: allowed, reason: reason };
            }
            case ProposalStatus.PENDING_REVIEW:
                return { allowed: allowed, reason: 'review' };
            case ProposalStatus.PENDING_NATIONAL_REVIEW:
                // The national-only narrowing that used to be a separate predicate here
                // (canApproveNationally, CZNA-552) is inside the backend's answer now.
                return { allowed: allowed, reason: 'national' };
            default:
                return null;
        }
    };
    if (isError) {
        return (React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.detail.loadError" }))));
    }
    if (isLoading || !proposal) {
        return React.createElement(ProposalGateLoading, null);
    }
    var gate = gateFor(proposal);
    // Closed BEFORE any permission question: no role reopens an APPROVED or REJECTED
    // proposal, so "ask your administrator for a role" would be advice that can never
    // work — the same dead end this ticket removes, one branch over.
    if (gate === null) {
        return (React.createElement(ProposalGateMessage, { testId: "proposal-edit-closed", heading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.edit.closed.heading" }), body: (React.createElement(FormattedMessage, { id: (_b = CLOSED_MESSAGES[proposal.status]) !== null && _b !== void 0 ? _b : 'ui-czna-authorities.proposals.edit.closed.other.message' })), backLabelId: BACK_LABEL, onClose: handleCancel }));
    }
    // CZNA-598 (blind review) — every branch that consults the authorization context now
    // sits INSIDE the refusal, and that placement is the fix.
    //
    // Until this ticket the verdict was COMPUTED from that context, so a slow or failed
    // fetch had to stop everything. It comes from `proposal.actions` now, and the context
    // is consulted only to WORD a refusal — so a cataloguer the backend allows gets their
    // form whether or not mod-czna-users answers. Keeping the old blocking branches would
    // leave an allowed edit hostage to a request the decision no longer needs, which is the
    // kind of dependency this ticket exists to remove.
    if (!gate.allowed) {
        // The wait stays, because the two refusals read differently ("ask your administrator"
        // vs "write to the originating library") and showing the wrong one and then swapping
        // it is worse than a moment of spinner.
        if (isAuthorizationLoading) {
            return React.createElement(ProposalGateLoading, null);
        }
        // The request FAILED. We can still say no — `actions` said so — but we cannot say
        // WHY, and guessing tells an authority admin to ask for a role they already hold.
        if (isAuthorizationError) {
            return (React.createElement(ProposalGateMessage, { testId: "proposal-edit-unavailable", heading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.edit.unavailable.heading" }), body: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.edit.unavailable.message" }), backLabelId: BACK_LABEL, onClose: handleCancel }));
        }
        var isInstitutionRefusal = gate.reason === 'institution';
        // CZNA-1576 — the institution sentence names the library, so it waits for the lookup
        // the same way it waits for the context above: a raw UUID that turns into "ABA001 –
        // Národní knihovna ČR" a moment later is the swap that wait exists to avoid. A FAILED
        // lookup does not wait — it falls through to the UUID, visibly. The role sentences
        // name no library and never wait for it.
        if (isInstitutionRefusal && isOrganizationLoading) {
            return React.createElement(ProposalGateLoading, null);
        }
        // The type is always named. "Nemáte oprávnění" with no object leaves the user
        // nothing concrete to ask the administrator for (CZNA-591).
        var type = intl.formatMessage({
            id: "ui-czna-authorities.recordType.".concat(proposal.recordType),
        });
        return (React.createElement(ProposalGateMessage, { testId: "proposal-edit-denied", heading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.edit.denied.heading" }), body: (React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.edit.denied.".concat(gate.reason, ".message"), 
                // CZNA-1576 — "write to the originator" names the originator. `hasInstitution`
                // lets the translation drop the parenthesis for a proposal with no
                // organization rather than print "(Bez instituce (import))"; unreachable today
                // (`isFromOriginatingInstitution(null)` is true), kept so the sentence cannot
                // break if that edge moves.
                values: isInstitutionRefusal
                    ? {
                        type: type,
                        institution: getOrganizationLabel(proposal.organizationId),
                        hasInstitution: proposal.organizationId ? 'yes' : 'no',
                    }
                    : { type: type } })), backLabelId: BACK_LABEL, onClose: handleCancel }));
    }
    // CZNA-552 — both review stages are "review mode"; the difference in who may act is
    // enforced by the backend and reflected in which buttons useProposalReviewActions draws.
    var isReviewMode = PENDING_PROPOSAL_STATUSES.includes(proposal.status);
    return (React.createElement(ProposalEditForm, { id: id, proposal: proposal, isReviewMode: isReviewMode, restoreToken: restoreToken, onCancel: handleCancel }));
};
export default ProposalEdit;
